import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { X, AlertCircle, Search, Info, ExternalLink, Inbox, ChevronRight } from 'lucide-react';
import { KeywordDataItem, KeywordDataResponse } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import Pagination from './Pagination';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  animations,
  zIndex,
} from '../styles/designTokens';
import { formatDateTimeKST } from '../utils/dateUtils';

// 오버레이
const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: ${zIndex.overlay};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  
  @media (max-width: 768px) {
    background: rgba(15, 23, 42, 0.6);
  }
`;

// 사이드 패널 컨테이너
const SidePanelContainer = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 800px;
  max-width: 90vw;
  height: 100vh;
  background: ${colors.semantic.background.primary};
  border-left: 1px solid ${colors.semantic.border.primary};
  display: flex;
  flex-direction: column;
  z-index: ${zIndex.modal};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform ${animations.duration.normal} ${animations.easing.smooth};
  box-shadow: ${shadows['2xl']};
  overflow: hidden;
  
  /* 모바일에서는 전체 화면 */
  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
    border-left: none;
  }
  
  /* 태블릿에서는 약간 좁게 */
  @media (min-width: 769px) and (max-width: 1024px) {
    width: 600px;
  }
  
  /* 큰 화면에서는 더 넓게 */
  @media (min-width: 1400px) {
    width: 900px;
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.semantic.border.secondary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.semantic.text.tertiary};
  }
`;

// 헤더
const PanelHeader = styled.div`
  padding: ${spacing[5]} ${spacing[5]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const PanelTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  flex: 1;
  min-width: 0;

  h2 {
    font-size: ${typography.fontSize['2xl']};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.semantic.text.primary};
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${spacing[2]};
    line-height: ${typography.lineHeight.tight};
  }

  p {
    margin: 0;
    font-size: ${typography.fontSize.sm};
    color: ${colors.semantic.text.secondary};
    line-height: ${typography.lineHeight.relaxed};
    max-width: 52ch;
  }
`;

const TitleIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.lg};
  background: ${colors.primary[50]};
  color: ${colors.primary[600]};
  border: 1px solid ${colors.primary[100]};
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  background: ${colors.semantic.background.primary};
  color: ${colors.semantic.text.secondary};
  cursor: pointer;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  flex-shrink: 0;
  
  &:hover {
    background: ${colors.semantic.background.tertiary};
    border-color: ${colors.primary[300]};
    color: ${colors.primary[600]};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// 콘텐츠 영역 — 패널 내 세로 스크롤 단일화 (목록·상세·페이지네이션 동일 스크롤 컨테이너)
const PanelContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${spacing[5]};
  gap: ${spacing[6]};

  @media (min-width: 769px) {
    gap: ${spacing[8]};
  }
`;

// 필터/태그 영역
const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const SectionEyebrow = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.tertiary};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ConditionStatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing[4]} ${spacing[6]};
`;

const ConditionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  flex: 1 1 280px;
  min-width: 0;
`;

const StatsGroup = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
`;

const FilterTag = styled.div<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  background: ${({ $isActive }) =>
    $isActive
      ? colors.primary[50]
      : colors.semantic.background.secondary};
  border: 1px solid
    ${({ $isActive }) =>
      $isActive
        ? colors.primary[300]
        : colors.semantic.border.primary};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${({ $isActive }) =>
    $isActive
      ? colors.primary[700]
      : colors.semantic.text.secondary};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  cursor: default;

  ${({ $isActive }) =>
    $isActive &&
    css`
      box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
    `}
`;

const FilterTagLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.semantic.text.tertiary};
  font-weight: ${typography.fontWeight.medium};
`;

const FilterTagValue = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.primary};
`;

const TotalCount = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding: ${spacing[3]} ${spacing[4]};
  min-width: 140px;
  background: ${colors.primary[50]};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.primary[200]};
`;

const TotalCountLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary[700]};
  letter-spacing: 0.02em;
`;

const TotalCountValue = styled.span`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  letter-spacing: -0.02em;
`;

// 테이블 래퍼 — 목록 + 페이지네이션만 포함 (상세는 형제로 분리)
const TableWrapper = styled.div`
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.semantic.border.primary};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${colors.semantic.background.primary};
`;

const TableContainer = styled.div`
  overflow-x: auto;
  overflow-y: visible;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.semantic.border.secondary};
    border-radius: 3px;
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSize.sm};
`;

const TableHead = styled.thead`
  background: ${colors.semantic.background.secondary};
  border-bottom: 2px solid ${colors.semantic.border.primary};
  position: sticky;
  top: 0;
  z-index: 5;
  box-shadow: 0 1px 0 ${colors.semantic.border.primary};

  th {
    padding: ${spacing[3]} ${spacing[4]};
    text-align: left;
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.semantic.text.primary};
    font-size: ${typography.fontSize.xs};
    white-space: nowrap;
    border-right: 1px solid ${colors.semantic.border.primary};
    letter-spacing: 0.02em;

    &:last-child {
      border-right: none;
    }
  }
`;

const ThHint = styled.span`
  display: block;
  margin-top: ${spacing[1]};
  font-weight: ${typography.fontWeight.normal};
  font-size: 10px;
  color: ${colors.semantic.text.tertiary};
  letter-spacing: 0;
  white-space: normal;
  line-height: 1.35;
  max-width: 140px;
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${colors.semantic.border.primary};
    transition:
      background-color ${animations.duration.fast} ${animations.easing.smooth},
      transform ${animations.duration.fast} ${animations.easing.smooth},
      box-shadow ${animations.duration.fast} ${animations.easing.smooth};

    &:hover {
      background: ${colors.semantic.background.secondary};
      transform: translateY(-2px);
      box-shadow: ${shadows.sm};
    }

    &[data-selected='true'] {
      background: ${colors.primary[50]};
      box-shadow:
        inset 4px 0 0 ${colors.primary[600]},
        0 0 0 1px ${colors.primary[200]};
    }

    &[data-selected='true']:hover {
      box-shadow:
        inset 4px 0 0 ${colors.primary[600]},
        ${shadows.md};
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: ${spacing[4]} ${spacing[5]};
    border-right: 1px solid ${colors.semantic.border.primary};
    vertical-align: top;
    color: ${colors.semantic.text.primary};
    font-size: ${typography.fontSize.sm};
    line-height: ${typography.lineHeight.relaxed};

    &:last-child {
      border-right: none;
    }
  }
`;

// 각 열별 스타일 컴포넌트
const TitleCell = styled.td`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.primary};
  max-width: 250px;
  min-width: 200px;
  
  @media (max-width: 768px) {
    max-width: 150px;
    min-width: 120px;
  }
`;

const AuthorCell = styled.td`
  max-width: 120px;
  min-width: 100px;
  
  @media (max-width: 768px) {
    max-width: 80px;
    min-width: 60px;
  }
`;

const DateTimeCell = styled.td`
  max-width: 160px;
  min-width: 140px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    max-width: 120px;
    min-width: 100px;
  }
`;

const AnalysisResultCell = styled.td`
  max-width: 100px;
  min-width: 80px;
  white-space: nowrap;
`;

const SummaryCell = styled.td`
  max-width: 350px;
  min-width: 250px;
  color: ${colors.semantic.text.secondary};
  line-height: ${typography.lineHeight.relaxed};
  
  @media (max-width: 768px) {
    max-width: 200px;
    min-width: 150px;
  }
`;

// 텍스트 말줄임 처리 공통 스타일
const EllipsisText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const EllipsisTextMulti = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  max-width: 100%;
`;

const TitleCellInner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[2]};
  min-width: 0;
`;

const RowHintChevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: ${colors.semantic.text.tertiary};
  opacity: 0.65;
  transition: color ${animations.duration.fast} ${animations.easing.smooth};

  tr:hover & {
    color: ${colors.primary[600]};
    opacity: 1;
  }

  tr[data-selected='true'] & {
    color: ${colors.primary[700]};
    opacity: 1;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TitleLink = styled.a`
  color: ${colors.semantic.text.primary};
  text-decoration: none;
  font-weight: ${typography.fontWeight.semibold};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  cursor: pointer;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  flex: 1;
  min-width: 0;
  
  &:hover {
    color: ${colors.primary[600]};
    text-decoration: underline;
  }
  
  &:visited {
    color: ${colors.primary[700]};
  }
`;


const AnalysisResultBadge = styled.span<{ result?: string | null }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  white-space: nowrap;
  background: ${({ result }) =>
    result === 'NEGATIVE'
      ? colors.error[50]
      : result === 'NORMAL'
      ? colors.success[50]
      : colors.semantic.background.tertiary};
  color: ${({ result }) =>
    result === 'NEGATIVE'
      ? colors.error[700]
      : result === 'NORMAL'
      ? colors.success[700]
      : colors.semantic.text.tertiary};
`;


// 하단 영역 (페이지네이션)
const Footer = styled.div`
  padding: ${spacing[4]} ${spacing[5]};
  border-top: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.secondary};
  flex-shrink: 0;
`;

// 로딩/에러/빈 상태
const StateContainer = styled.div`
  padding: ${spacing[10]} ${spacing[5]};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[3]};
  color: ${colors.semantic.text.tertiary};
  font-size: ${typography.fontSize.sm};
  min-height: 200px;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.full};
  border: 3px solid ${colors.semantic.border.primary};
  border-top-color: ${colors.primary[500]};
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const DetailHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 6;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[5]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.primary};
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.06);
`;

const DetailHeaderText = styled.div`
  min-width: 0;
  flex: 1;
`;

const DetailTitle = styled.h3`
  margin: 0 0 ${spacing[1]} 0;
  font-size: 1.125rem;
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  letter-spacing: -0.02em;
`;

const DetailTitleSub = styled.p`
  margin: 0;
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  line-height: ${typography.lineHeight.relaxed};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const DetailCloseRow = styled.button`
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary[700]};
  background: ${colors.semantic.background.primary};
  border: 2px solid ${colors.primary[300]};
  cursor: pointer;
  border-radius: ${borderRadius.md};
  flex-shrink: 0;
  transition:
    color ${animations.duration.fast} ${animations.easing.smooth},
    border-color ${animations.duration.fast} ${animations.easing.smooth},
    background ${animations.duration.fast} ${animations.easing.smooth},
    box-shadow ${animations.duration.fast} ${animations.easing.smooth};

  &:hover {
    color: ${colors.primary[800]};
    border-color: ${colors.primary[500]};
    background: ${colors.primary[50]};
    box-shadow: ${shadows.sm};
  }
`;

const DetailScroll = styled.div`
  padding: ${spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const DetailSubBlock = styled.div`
  padding: ${spacing[5]};
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const DetailSubBlockTitle = styled.h4`
  margin: 0;
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  letter-spacing: 0.02em;
`;

const DetailFieldGrid = styled.div`
  display: grid;
  gap: ${spacing[4]};

  @media (min-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DetailField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

const DetailFieldFull = styled(DetailField)`
  @media (min-width: 560px) {
    grid-column: 1 / -1;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.tertiary};
  letter-spacing: 0.01em;
`;

const DetailValue = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.primary};
  line-height: ${typography.lineHeight.relaxed};
  word-break: break-word;
  white-space: pre-wrap;
`;

const DetailValueLarge = styled(DetailValue)`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.semantic.text.primary};
`;

const DetailLink = styled.a`
  display: inline-flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  color: ${colors.primary[600]};
  word-break: break-all;
  font-weight: ${typography.fontWeight.medium};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    opacity: 0.85;
  }

  &:hover {
    text-decoration: underline;
    color: ${colors.primary[700]};
  }
`;

const JsonPre = styled.pre`
  margin: 0;
  padding: ${spacing[4]};
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.relaxed};
  font-family: ${typography.fontFamily.mono};
  background: ${colors.secondary[100]};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: 0 0 ${borderRadius.md} ${borderRadius.md};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${colors.semantic.text.secondary};
  tab-size: 2;

  @media (min-width: 768px) {
    font-size: ${typography.fontSize.sm};
    padding: ${spacing[5]};
  }
`;

const HintDetails = styled.details`
  border: 1px solid ${colors.primary[100]};
  border-radius: ${borderRadius.lg};
  background: ${colors.primary[50]};
  flex-shrink: 0;
`;

const HintSummary = styled.summary`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary[800]};
  cursor: pointer;
  list-style: none;
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background: ${colors.primary[100]};
  }
`;

const HintBody = styled.div`
  padding: 0 ${spacing[4]} ${spacing[3]} ${spacing[4]};
  padding-left: calc(${spacing[4]} + 22px);
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  line-height: ${typography.lineHeight.relaxed};

  strong {
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.semantic.text.primary};
  }
`;

const JsonDisclosure = styled.details`
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  background: ${colors.secondary[50]};
  flex-shrink: 0;
`;

const JsonSummary = styled.summary`
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.secondary};
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: ${colors.semantic.text.primary};
    background: ${colors.secondary[100]};
  }
`;

const DetailSectionWrap = styled.section`
  flex-shrink: 0;
  margin-top: ${spacing[2]};
  padding-top: ${spacing[6]};
  border-top: 2px solid ${colors.primary[200]};
`;

const DetailSectionCard = styled.div`
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.primary[200]};
  background: ${colors.semantic.background.primary};
  box-shadow: ${shadows.md};
  overflow: hidden;
`;

const DetailSectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary[700]};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ScrapPreview = styled.div`
  margin: 0;
  padding: ${spacing[4]};
  font-size: ${typography.fontSize.sm};
  line-height: ${typography.lineHeight.relaxed};
  color: ${colors.semantic.text.primary};
  background: ${colors.semantic.background.secondary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  max-height: 12rem;
  overflow: hidden;
  position: relative;
  white-space: pre-wrap;
  word-break: break-word;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2rem;
    background: linear-gradient(transparent, ${colors.semantic.background.secondary});
    pointer-events: none;
  }
`;

const KeywordChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const KeywordChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.primary[800]};
  background: ${colors.primary[50]};
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.full};
`;

const AnalysisHumanDivider = styled.hr`
  margin: ${spacing[3]} 0;
  border: none;
  border-top: 1px dashed ${colors.semantic.border.primary};
`;

interface KeywordDataSidePanelProps {
  userIdx: number;
  keywordIdx: number;
  keywordText: string;
  brandName: string;
  targetSite: string;
  isOpen: boolean;
  onClose: () => void;
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, (_, v) => (typeof v === 'bigint' ? String(v) : v), 2);
  } catch {
    return String(value);
  }
}

function nonEmptyRecord(obj: Record<string, unknown> | null | undefined): boolean {
  return !!obj && typeof obj === 'object' && Object.keys(obj).length > 0;
}

function truncateForHeader(text: string, maxChars: number): string {
  const t = text.trim();
  if (t.length <= maxChars) return t;
  return `${t.slice(0, maxChars)}…`;
}

/** scrap/analy 본문 미리보기용 — API 변경 없이 표시만 정리 */
function stripHtmlToPlain(raw: string): string {
  if (!raw || typeof raw !== 'string') return '';
  const noTags = raw.replace(/<[^>]*>/g, ' ');
  return noTags
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeBadgeResult(v: unknown): 'NORMAL' | 'NEGATIVE' | null {
  if (v === null || v === undefined) return null;
  const s = String(v).toUpperCase();
  if (s === 'NEGATIVE' || s === '부정') return 'NEGATIVE';
  if (s === 'NORMAL' || s === '정상') return 'NORMAL';
  return null;
}

function extractAnalyKeywords(analy: Record<string, unknown>): string[] {
  const raw = analy.keywords ?? analy.keyword_list ?? analy.keywordList;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) out.push(item.trim());
    else if (item && typeof item === 'object' && 'word' in item && typeof (item as { word: unknown }).word === 'string') {
      const w = (item as { word: string }).word.trim();
      if (w) out.push(w);
    } else if (item && typeof item === 'object' && 'keyword' in item && typeof (item as { keyword: unknown }).keyword === 'string') {
      const w = (item as { keyword: string }).keyword.trim();
      if (w) out.push(w);
    }
  }
  return out;
}

function extractAnalySummary(analy: Record<string, unknown>): string | null {
  const s =
    analy.summary ??
    analy.Summary ??
    analy.summary_text ??
    analy['분석요약'];
  if (typeof s === 'string' && s.trim()) return s.trim();
  return null;
}

function scrapPlainPreview(scrap: Record<string, unknown> | null | undefined): string | null {
  if (!scrap) return null;
  const content = scrap.content ?? scrap.body ?? scrap.text ?? scrap['본문'];
  if (typeof content !== 'string' || !content.trim()) return null;
  const plain = stripHtmlToPlain(content);
  return plain || null;
}

function formatCollectedAtDisplay(value?: string | null): string {
  if (!value || !String(value).trim()) return '-';
  const raw = String(value).trim();
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return formatDateTimeKST(raw);
  }
  return raw;
}

const KeywordDataSidePanel: React.FC<KeywordDataSidePanelProps> = ({
  userIdx,
  keywordIdx,
  keywordText,
  brandName,
  targetSite,
  isOpen,
  onClose,
}) => {
  const [data, setData] = useState<KeywordDataResponse | null>(null);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<KeywordDataItem | null>(null);

  // ESC: 상세 열림 시 상세만 닫고, 없을 때만 패널 닫기 / body 스크롤
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (!isOpen || e.key !== 'Escape') return;
      if (selectedItem) {
        e.preventDefault();
        setSelectedItem(null);
      } else {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, selectedItem]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await CustomerService.getKeywordData({
          userIdx,
          keywordIdx,
          page,
          limit: 15,
        });

        setData(response);
      } catch (err: any) {
        console.error('키워드 수집 데이터 조회 실패:', err);
        setError(err.message || '키워드 수집 데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, userIdx, keywordIdx, page]);

  useEffect(() => {
    setSelectedItem(null);
  }, [userIdx, keywordIdx, page]);

  useEffect(() => {
    if (!data?.items?.length || !selectedItem) return;
    const stillThere = data.items.some((i) => i._id === selectedItem._id);
    if (!stillThere) setSelectedItem(null);
  }, [data, selectedItem]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowClick = (e: React.MouseEvent, item: KeywordDataItem) => {
    if ((e.target as HTMLElement).closest('a')) return;
    setSelectedItem((prev) => (prev?._id === item._id ? null : item));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <SidePanelContainer
        $isOpen={isOpen}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyword-data-panel-title"
      >
        <PanelHeader>
          <PanelTitle>
            <h2 id="keyword-data-panel-title">
              <TitleIconWrap aria-hidden>
                <Search />
              </TitleIconWrap>
              키워드 수집 데이터
            </h2>
            <p>
              이 키워드로 수집된 게시글 목록입니다. <strong>행을 클릭</strong>하면 스크랩·분석
              원본을 포함한 상세 패널이 아래에 열립니다. 제목은 새 탭 링크입니다.
            </p>
          </PanelTitle>
          <CloseButton onClick={onClose} aria-label="패널 닫기">
            <X />
          </CloseButton>
        </PanelHeader>

        <PanelContent>
          <FilterSection>
            <ConditionStatsRow>
              <ConditionGroup>
                <SectionEyebrow>수집 조건</SectionEyebrow>
                <FilterTags>
                  <FilterTag $isActive={false}>
                    <FilterTagLabel>브랜드</FilterTagLabel>
                    <FilterTagValue>{brandName}</FilterTagValue>
                  </FilterTag>
                  <FilterTag $isActive={true}>
                    <FilterTagLabel>키워드</FilterTagLabel>
                    <FilterTagValue>{keywordText}</FilterTagValue>
                  </FilterTag>
                  <FilterTag $isActive={false}>
                    <FilterTagLabel>타겟사이트</FilterTagLabel>
                    <FilterTagValue>{targetSite}</FilterTagValue>
                  </FilterTag>
                </FilterTags>
              </ConditionGroup>
              <StatsGroup>
                {data && (
                  <TotalCount>
                    <TotalCountLabel>총 수집량</TotalCountLabel>
                    <TotalCountValue>{data.total.toLocaleString('ko-KR')}건</TotalCountValue>
                  </TotalCount>
                )}
              </StatsGroup>
            </ConditionStatsRow>
          </FilterSection>

          <TableWrapper>
            {loading ? (
              <StateContainer>
                <Spinner />
                <div>데이터를 불러오는 중입니다...</div>
              </StateContainer>
            ) : error ? (
              <StateContainer>
                <AlertCircle size={20} color={colors.error[600]} />
                <div style={{ color: colors.error[600] }}>{error}</div>
              </StateContainer>
            ) : !data || !data.items || data.items.length === 0 ? (
              <StateContainer>
                <Inbox size={40} strokeWidth={1.25} color={colors.semantic.text.tertiary} aria-hidden />
                <div style={{ textAlign: 'center', maxWidth: '280px' }}>
                  <strong style={{ color: colors.semantic.text.secondary, display: 'block', marginBottom: spacing[1] }}>
                    아직 수집된 게시글이 없습니다
                  </strong>
                  <span>수집 주기·키워드 설정을 확인하거나, 잠시 후 다시 조회해 보세요.</span>
                </div>
              </StateContainer>
            ) : (
              <>
                <HintDetails>
                  <HintSummary>
                    <Info size={18} strokeWidth={2} aria-hidden />
                    목록 사용 안내 · 클릭하여 펼치기
                  </HintSummary>
                  <HintBody role="note">
                    <strong>행 전체</strong>를 클릭하면 아래 <strong>선택한 건 상세</strong>가 열립니다.{' '}
                    <strong>제목(링크)</strong>만 클릭하면 원문이 <strong>새 탭</strong>에서 열리며 상세는 열리지 않습니다.
                  </HintBody>
                </HintDetails>
                <TableContainer>
                  <DataTable>
                    <TableHead>
                      <tr>
                        <th scope="col">
                          제목
                          <ThHint>원문 링크</ThHint>
                        </th>
                        <th scope="col">작성자</th>
                        <th scope="col">작성시간</th>
                        <th scope="col">
                          분석
                          <ThHint>부정·정상</ThHint>
                        </th>
                        <th scope="col">분석요약</th>
                      </tr>
                    </TableHead>
                    <TableBody>
                      {data.items.map((item: KeywordDataItem) => (
                        <tr
                          key={item._id}
                          data-selected={selectedItem?._id === item._id ? 'true' : 'false'}
                          aria-selected={selectedItem?._id === item._id}
                          title="행을 클릭하면 아래에 상세가 열립니다. 제목은 새 탭 링크입니다."
                          onClick={(e) => handleRowClick(e, item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <TitleCell>
                            <TitleCellInner>
                              {item.링크 ? (
                                <TitleLink
                                  href={item.링크}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={item.제목 || ''}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {item.제목 || '-'}
                                </TitleLink>
                              ) : (
                                <EllipsisText title={item.제목 || ''}>
                                  {item.제목 || '-'}
                                </EllipsisText>
                              )}
                              <RowHintChevron aria-hidden>
                                <ChevronRight />
                              </RowHintChevron>
                            </TitleCellInner>
                          </TitleCell>
                          <AuthorCell>
                            <EllipsisText title={item.작성자 || ''}>
                              {item.작성자 || '-'}
                            </EllipsisText>
                          </AuthorCell>
                          <DateTimeCell>
                            {item.작성시간 ? formatDateTimeKST(item.작성시간) : '-'}
                          </DateTimeCell>
                          <AnalysisResultCell>
                            <AnalysisResultBadge result={item.분석결과}>
                              {item.분석결과 === 'NEGATIVE'
                                ? '부정'
                                : item.분석결과 === 'NORMAL'
                                ? '정상'
                                : '분석 대기'}
                            </AnalysisResultBadge>
                          </AnalysisResultCell>
                          <SummaryCell>
                            {item.분석요약 ? (
                              <EllipsisTextMulti title={item.분석요약}>
                                {item.분석요약}
                              </EllipsisTextMulti>
                            ) : (
                              '-'
                            )}
                          </SummaryCell>
                        </tr>
                      ))}
                    </TableBody>
                  </DataTable>
                </TableContainer>

                <Footer>
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.total_pages}
                    total={data.total}
                    limit={data.limit}
                    onPageChange={handlePageChange}
                  />
                </Footer>
              </>
            )}
          </TableWrapper>

          {selectedItem && data && data.items.length > 0 && (
            <DetailSectionWrap aria-label="선택한 수집 데이터 상세" aria-live="polite">
              <DetailSectionLabel>선택한 건 · 상세</DetailSectionLabel>
              <DetailSectionCard>
                <DetailHeader>
                  <DetailHeaderText>
                    <DetailTitle>상세 데이터</DetailTitle>
                    <DetailTitleSub title={selectedItem.제목?.trim() || undefined}>
                      {selectedItem.제목?.trim()
                        ? truncateForHeader(selectedItem.제목, 72)
                        : '제목 없음 · 메타·원본 필드를 확인하세요'}
                    </DetailTitleSub>
                  </DetailHeaderText>
                  <DetailCloseRow
                    type="button"
                    onClick={() => setSelectedItem(null)}
                    aria-label="선택한 건 상세 닫기"
                  >
                    상세 닫기
                  </DetailCloseRow>
                </DetailHeader>
                <DetailScroll>
                  {(() => {
                    const analyObj =
                      selectedItem.analy && typeof selectedItem.analy === 'object'
                        ? (selectedItem.analy as Record<string, unknown>)
                        : null;
                    const badgeFromAnaly = analyObj ? normalizeBadgeResult(analyObj.result) : null;
                    const badgeResult = badgeFromAnaly ?? selectedItem.분석결과;
                    const summaryProse =
                      (analyObj && extractAnalySummary(analyObj)) ||
                      (selectedItem.분석요약?.trim() ? selectedItem.분석요약 : null);
                    const keywordList = analyObj ? extractAnalyKeywords(analyObj) : [];
                    const scrapPrev =
                      selectedItem.scrap && typeof selectedItem.scrap === 'object'
                        ? scrapPlainPreview(selectedItem.scrap as Record<string, unknown>)
                        : null;
                    return (
                      <>
                        <DetailSubBlock>
                          <DetailSubBlockTitle>게시물 · 수집 정보</DetailSubBlockTitle>
                          <DetailFieldGrid>
                            <DetailFieldFull>
                              <DetailLabel>제목</DetailLabel>
                              <DetailValueLarge>
                                {selectedItem.제목?.trim() ? selectedItem.제목 : '-'}
                              </DetailValueLarge>
                            </DetailFieldFull>
                            <DetailField>
                              <DetailLabel>작성자</DetailLabel>
                              <DetailValue>{selectedItem.작성자?.trim() ? selectedItem.작성자 : '-'}</DetailValue>
                            </DetailField>
                            <DetailField>
                              <DetailLabel>작성시간</DetailLabel>
                              <DetailValue>
                              {selectedItem.작성시간
                                ? formatDateTimeKST(selectedItem.작성시간)
                                : '-'}
                            </DetailValue>
                            </DetailField>
                            <DetailFieldFull>
                              <DetailLabel>수집일시 (KST)</DetailLabel>
                              <DetailValue>{formatCollectedAtDisplay(selectedItem.수집일시)}</DetailValue>
                            </DetailFieldFull>
                            <DetailFieldFull>
                              <DetailLabel>원문 링크</DetailLabel>
                              <DetailValue>
                                {selectedItem.링크 ? (
                                  <DetailLink href={selectedItem.링크} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink size={14} aria-hidden />
                                    {selectedItem.링크}
                                  </DetailLink>
                                ) : (
                                  '-'
                                )}
                              </DetailValue>
                            </DetailFieldFull>
                          </DetailFieldGrid>
                        </DetailSubBlock>

                        <DetailSubBlock>
                          <DetailSubBlockTitle>분석</DetailSubBlockTitle>
                          <DetailField>
                            <DetailLabel>분석 결과</DetailLabel>
                            <DetailValue>
                              <AnalysisResultBadge result={badgeResult}>
                                {badgeResult === 'NEGATIVE'
                                  ? '부정'
                                  : badgeResult === 'NORMAL'
                                  ? '정상'
                                  : '분석 대기'}
                              </AnalysisResultBadge>
                            </DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>요약</DetailLabel>
                            <DetailValue>{summaryProse || '-'}</DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>핵심 키워드</DetailLabel>
                            <DetailValue>
                              {keywordList.length > 0 ? (
                                <KeywordChipRow>
                                  {keywordList.map((kw) => (
                                    <KeywordChip key={kw}>{kw}</KeywordChip>
                                  ))}
                                </KeywordChipRow>
                              ) : (
                                '-'
                              )}
                            </DetailValue>
                          </DetailField>
                          <AnalysisHumanDivider />
                          {selectedItem.ai_type !== undefined &&
                            selectedItem.ai_type !== null &&
                            selectedItem.ai_type !== '' && (
                              <DetailField>
                                <DetailLabel>AI 유형</DetailLabel>
                                <DetailValue>{String(selectedItem.ai_type)}</DetailValue>
                              </DetailField>
                            )}
                          {nonEmptyRecord(selectedItem.analy) && (
                            <JsonDisclosure>
                              <JsonSummary>원본 데이터 (analy JSON) 보기</JsonSummary>
                              <JsonPre>{safeJsonStringify(selectedItem.analy)}</JsonPre>
                            </JsonDisclosure>
                          )}
                        </DetailSubBlock>

                        <DetailSubBlock>
                          <DetailSubBlockTitle>키워드 맥락</DetailSubBlockTitle>
                          <DetailField>
                            <DetailLabel>타겟 사이트 · 브랜드 · 키워드</DetailLabel>
                            <DetailValue>
                              {selectedItem.타겟사이트 || '-'} · {selectedItem.브랜드 || '-'} ·{' '}
                              {selectedItem.키워드 || '-'}
                            </DetailValue>
                          </DetailField>
                        </DetailSubBlock>

                        <DetailSubBlock>
                          <DetailSubBlockTitle>식별자 · 스크랩 원본</DetailSubBlockTitle>
                          <DetailField>
                            <DetailLabel>문서 ID</DetailLabel>
                            <DetailValue>
                              <span
                                style={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.xs,
                                }}
                              >
                                {selectedItem._id}
                              </span>
                            </DetailValue>
                          </DetailField>
                          {scrapPrev && (
                            <DetailField>
                              <DetailLabel>본문·스크랩 미리보기 (태그 제거)</DetailLabel>
                              <ScrapPreview>{scrapPrev}</ScrapPreview>
                            </DetailField>
                          )}
                          {nonEmptyRecord(selectedItem.scrap) && (
                            <JsonDisclosure>
                              <JsonSummary>원본 데이터 (scrap JSON) 보기</JsonSummary>
                              <JsonPre>{safeJsonStringify(selectedItem.scrap)}</JsonPre>
                            </JsonDisclosure>
                          )}
                        </DetailSubBlock>
                      </>
                    );
                  })()}
                </DetailScroll>
              </DetailSectionCard>
            </DetailSectionWrap>
          )}
        </PanelContent>
      </SidePanelContainer>
    </>
  );
};

export default KeywordDataSidePanel;

